import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Trophy, Flame, Coins, Medal, EyeOff, Eye } from '../components/CustomIcons';

const MEDAL_COLORS = { 1: '#F59E0B', 2: '#9CA3AF', 3: '#B45309' };

function RankBadge({ rank }) {
  const color = MEDAL_COLORS[rank];
  if (color) {
    return (
      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `${color}22`, border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Medal size={15} color={color} />
      </div>
    );
  }
  return (
    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>{rank}</span>
    </div>
  );
}

function LeaderRow({ entry, isPrivate }) {
  const borderColor = entry.is_current_user ? '#F97316' : 'transparent';
  const bg = entry.is_current_user ? 'rgba(249,115,22,0.05)' : 'transparent';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '13px 18px', background: bg, borderLeft: `3px solid ${borderColor}`, borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.15s' }}
      data-testid={entry.is_current_user ? 'leaderboard-my-row' : `leaderboard-row-${entry.rank}`}>
      <RankBadge rank={entry.rank} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: entry.is_current_user ? 700 : 500, fontSize: '14px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.name}
          </span>
          {entry.is_current_user && (
            <span style={{ fontSize: '10px', background: '#F97316', color: '#111', borderRadius: '6px', padding: '1px 7px', fontWeight: 700, letterSpacing: '0.03em', flexShrink: 0 }}>YOU</span>
          )}
          {isPrivate && (
            <span style={{ fontSize: '10px', color: 'var(--text-dim)', flexShrink: 0 }}>• private</span>
          )}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.pathway || '—'} · Level {entry.level}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '17px', color: MEDAL_COLORS[entry.rank] || 'var(--text-primary)' }}>
          {entry.metric_value?.toLocaleString()}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-dim)' }}>{entry.metric_label}</div>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { authFetch, user } = useAuth();
  const [tab, setTab] = useState('credits');
  const [data, setData] = useState({ credits: null, streak: null });
  const [optOut, setOptOut] = useState(user?.profile?.leaderboard_opt_out || false);
  const [toggling, setToggling] = useState(false);
  const [loading, setLoading] = useState(true);

  const isLearner = user?.role === 'learner';
  const facilityLabel = user?.profile?.facility || '';

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authFetch('/gamification/leaderboard?metric=credits&limit=10'),
      authFetch('/gamification/leaderboard?metric=streak_days&limit=10'),
    ]).then(([cR, sR]) => {
      setData({ credits: cR.data, streak: sR.data });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptOut = async () => {
    setToggling(true);
    try {
      const r = await authFetch('/gamification/opt-out', { method: 'PUT' });
      setOptOut(r.data.leaderboard_opt_out);
      // Refetch board immediately so opt-out state is reflected without page refresh
      const [cR, sR] = await Promise.all([
        authFetch('/gamification/leaderboard?metric=credits&limit=10'),
        authFetch('/gamification/leaderboard?metric=streak_days&limit=10'),
      ]);
      setData({ credits: cR.data, streak: sR.data });
    } catch (e) {
      console.error(e);
    } finally {
      setToggling(false);
    }
  };

  const current = tab === 'credits' ? data.credits : data.streak;
  const metricKey = tab === 'credits' ? 'credits' : 'streak_days';
  const metricLabel = tab === 'credits' ? 'credits' : 'day streak';

  const enrichBoard = (board) => board.map(e => ({
    ...e,
    metric_value: e[metricKey],
    metric_label: metricLabel,
  }));

  const myEntry = current?.current_user ? {
    ...current.current_user,
    metric_value: current.current_user[metricKey],
    metric_label: metricLabel,
  } : null;

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="leaderboard-page">
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <Trophy size={22} color="#F59E0B" />
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Leaderboard</h1>
          </div>
          {facilityLabel && (
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
              {facilityLabel} · {current?.total_participants || 0} participants
            </p>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-surface)', borderRadius: '10px', padding: '4px', marginBottom: '20px', width: 'fit-content' }}>
          {[{ key: 'credits', icon: Coins, label: 'Top Credits' }, { key: 'streak', icon: Flame, label: 'Top Streak' }].map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setTab(key)}
              data-testid={`leaderboard-tab-${key}`}
              style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none', background: tab === key ? '#F97316' : 'transparent', color: tab === key ? '#111' : '#666', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {/* Your Rank (private — always visible for learners) */}
        {isLearner && myEntry && !current?.user_in_board && (
          <div style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '12px', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{ padding: '8px 18px', fontSize: '11px', fontWeight: 700, color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your Rank</div>
            <LeaderRow entry={myEntry} isPrivate={optOut} />
          </div>
        )}

        {/* Main Board */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                Top {current?.board?.length || 0}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
                {tab === 'credits' ? 'Total Credits Earned' : 'Current Day Streak'}
              </span>
            </div>
            {(current?.board?.length || 0) === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '14px' }}>
                No participants yet. Be the first on the board!
              </div>
            ) : (
              enrichBoard(current.board).map(entry => (
                <LeaderRow key={entry.user_id} entry={entry} />
              ))
            )}
          </div>
        )}

        {/* Opt-out toggle — learners only */}
        {isLearner && (
          <div style={{ marginTop: '24px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                {optOut ? <EyeOff size={14} color="var(--text-muted)" /> : <Eye size={14} color="#F97316" />}
                {optOut ? 'You are hidden from the leaderboard' : 'You are visible on the leaderboard'}
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-dim)', margin: 0, lineHeight: 1.5 }}>
                {optOut ? 'Your rank is private. Only you can see it in the "Your Rank" section.' : 'Your name appears publicly. Toggle to hide yourself.'}
              </p>
            </div>
            <button onClick={handleOptOut} disabled={toggling}
              data-testid="leaderboard-opt-out-btn"
              style={{ flexShrink: 0, background: optOut ? 'var(--orange-subtle)' : 'var(--surface-alt)', border: `1px solid ${optOut ? 'rgba(232,119,34,0.3)' : 'var(--border)'}`, borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', color: optOut ? '#F97316' : 'var(--text-secondary)', fontSize: '13px', fontWeight: 600, opacity: toggling ? 0.5 : 1 }}>
              {toggling ? '…' : optOut ? 'Show me' : 'Hide me'}
            </button>
          </div>
        )}

        {!isLearner && (
          <p style={{ marginTop: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--text-dim)' }}>
            Learners can opt out of the public board — their rank is always visible to them privately.
          </p>
        )}
      </div>
    </div>
  );
}
